import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, IconButton,
  Chip, Autocomplete, Select, MenuItem, FormControl, Switch, Popover, Link as MLink,
} from '@mui/material';
import AddRounded from '@mui/icons-material/AddRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CategoryRounded from '@mui/icons-material/CategoryRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import DeleteRounded from '@mui/icons-material/DeleteRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import CalculateRounded from '@mui/icons-material/CalculateRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import LocalMallRounded from '@mui/icons-material/LocalMallRounded';
import CheckroomRounded from '@mui/icons-material/CheckroomRounded';
import FlightRounded from '@mui/icons-material/FlightRounded';
import LocalHospitalRounded from '@mui/icons-material/LocalHospitalRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import MovieRounded from '@mui/icons-material/MovieRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import CalendarTodayRounded from '@mui/icons-material/CalendarTodayRounded';
import PersonRounded from '@mui/icons-material/PersonRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import CreditCardRounded from '@mui/icons-material/CreditCardRounded';
import LocalOfferRounded from '@mui/icons-material/LocalOfferRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import NotificationsActiveRounded from '@mui/icons-material/NotificationsActiveRounded';
import Swal from 'sweetalert2';
import {
  createPayment, updatePayment, deletePayment, listProjects, listAssignees,
  PaymentItem, PaymentReceipt, PaymentMethod,
} from '../../services/api';
import FormDialogShell, { FieldCard, gradientButtonSx } from '../ui/FormDialogShell';

type OptionItem = { _id: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editing: PaymentItem | null;
};

const TYPE_META = {
  income: { label: 'Income', icon: ArrowDownwardRounded, color: '#16A34A', bg: '#DCFCE7', grad: ['#11998E', '#38EF7D'] as [string, string] },
  expense: { label: 'Expense', icon: ArrowUpwardRounded, color: '#DC2626', bg: '#FCE7EC', grad: ['#DC2626', '#F45C43'] as [string, string] },
  transfer: { label: 'Transfer', icon: SwapHorizRounded, color: '#2563EB', bg: '#DBEAFE', grad: ['#2563EB', '#60A5FA'] as [string, string] },
} as const;

const CATEGORIES = [
  { name: 'Utilities', icon: BoltRounded, color: '#EF4444' },
  { name: 'Food', icon: LocalMallRounded, color: '#F59E0B' },
  { name: 'Shopping', icon: CheckroomRounded, color: '#6366F1' },
  { name: 'Travel', icon: FlightRounded, color: '#06B6D4' },
  { name: 'Health', icon: LocalHospitalRounded, color: '#EC4899' },
  { name: 'Education', icon: SchoolRounded, color: '#8B5CF6' },
  { name: 'Entertainment', icon: MovieRounded, color: '#F97316' },
  { name: 'Bills', icon: ReceiptLongRounded, color: '#0EA5E9' },
  { name: 'Salary', icon: PaymentsRounded, color: '#22C55E' },
  { name: 'Other', icon: MoreHorizRounded, color: '#64748B' },
];

const PAYMENT_METHODS: PaymentMethod[] = ['Cash', 'Bank Transfer', 'UPI', 'Credit Card', 'Debit Card', 'Card', 'Cheque', 'Other'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const toDateInput = (v?: string) => (v ? new Date(v).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10));
const toLocalDateTimeInput = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtSize = (bytes?: number) => {
  if (!bytes) return '';
  return bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ==================== A small 4-function calculator popover ==================== */
function CalculatorPopover({ anchorEl, onClose, onApply }: { anchorEl: HTMLElement | null; onClose: () => void; onApply: (value: number) => void }) {
  const [display, setDisplay] = useState('0');
  const [prev, setPrev] = useState<number | null>(null);
  const [op, setOp] = useState<string | null>(null);
  const [fresh, setFresh] = useState(true);

  const inputDigit = (d: string) => {
    setDisplay((cur) => (fresh || cur === '0' ? d : cur + d));
    setFresh(false);
  };
  const inputDot = () => {
    setDisplay((cur) => (fresh ? '0.' : cur.includes('.') ? cur : `${cur}.`));
    setFresh(false);
  };
  const compute = (a: number, b: number, operator: string) => {
    switch (operator) {
      case '+': return a + b;
      case '-': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? 0 : a / b;
      default: return b;
    }
  };
  const chooseOp = (operator: string) => {
    const current = parseFloat(display);
    if (prev !== null && op) {
      const result = compute(prev, current, op);
      setPrev(result);
      setDisplay(String(result));
    } else {
      setPrev(current);
    }
    setOp(operator);
    setFresh(true);
  };
  const equals = () => {
    if (prev === null || !op) return;
    const result = compute(prev, parseFloat(display), op);
    setDisplay(String(result));
    setPrev(null);
    setOp(null);
    setFresh(true);
  };
  const clear = () => { setDisplay('0'); setPrev(null); setOp(null); setFresh(true); };
  const backspace = () => setDisplay((cur) => (cur.length > 1 ? cur.slice(0, -1) : '0'));

  const buttons: Array<{ label: string; onClick: () => void; variant?: 'op' | 'eq' }> = [
    { label: 'C', onClick: clear },
    { label: '⌫', onClick: backspace },
    { label: '÷', onClick: () => chooseOp('÷'), variant: 'op' },
    { label: '×', onClick: () => chooseOp('×'), variant: 'op' },
    { label: '7', onClick: () => inputDigit('7') },
    { label: '8', onClick: () => inputDigit('8') },
    { label: '9', onClick: () => inputDigit('9') },
    { label: '-', onClick: () => chooseOp('-'), variant: 'op' },
    { label: '4', onClick: () => inputDigit('4') },
    { label: '5', onClick: () => inputDigit('5') },
    { label: '6', onClick: () => inputDigit('6') },
    { label: '+', onClick: () => chooseOp('+'), variant: 'op' },
    { label: '1', onClick: () => inputDigit('1') },
    { label: '2', onClick: () => inputDigit('2') },
    { label: '3', onClick: () => inputDigit('3') },
    { label: '=', onClick: equals, variant: 'eq' },
    { label: '0', onClick: () => inputDigit('0') },
    { label: '.', onClick: inputDot },
  ];

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 1.5, width: 220 }}>
        <Box sx={{ bgcolor: 'action.hover', p: 1.5, mb: 1, textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>{display}</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 0.5 }}>
          {buttons.map((b) => (
            <Button
              key={b.label}
              size="small"
              variant={b.variant ? 'contained' : 'outlined'}
              color={b.variant === 'eq' ? 'primary' : b.variant === 'op' ? 'secondary' : 'inherit'}
              onClick={b.onClick}
              sx={{ minWidth: 0, px: 0 }}
            >
              {b.label}
            </Button>
          ))}
        </Box>
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ mt: 1 }}
          onClick={() => { onApply(parseFloat(display) || 0); onClose(); }}
        >
          Use this value
        </Button>
      </Box>
    </Popover>
  );
}

const PaymentFormDialog = ({ open, onClose, onSaved, editing }: Props) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'income' | 'expense' | 'transfer'>('expense');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(toDateInput());
  const [personSel, setPersonSel] = useState<OptionItem | string | null>(null);
  const [personOptions, setPersonOptions] = useState<OptionItem[]>([]);
  const [projectSel, setProjectSel] = useState<OptionItem | null>(null);
  const [projectOptions, setProjectOptions] = useState<OptionItem[]>([]);
  const [method, setMethod] = useState<PaymentMethod | ''>('');
  const [tags, setTags] = useState<string[]>([]);

  const [existingReceipts, setExistingReceipts] = useState<PaymentReceipt[]>([]);
  const [removeReceiptIds, setRemoveReceiptIds] = useState<string[]>([]);
  const [newReceipts, setNewReceipts] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [reminderEnabled, setReminderEnabled] = useState(false);
  const [reminderAt, setReminderAt] = useState('');

  const [calcAnchor, setCalcAnchor] = useState<HTMLElement | null>(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    title?: string; amount?: string; date?: string; description?: string; reminderAt?: string;
  }>({});
  const clearField = (k: keyof typeof fieldErrors) => setFieldErrors((p) => ({ ...p, [k]: undefined }));

  const isEdit = !!editing;

  useEffect(() => {
    if (!open) return;
    listProjects().then((ps: any[]) => setProjectOptions((ps || []).map((p) => ({ _id: p._id, name: p.name })))).catch(() => setProjectOptions([]));
    listAssignees().then((as: any[]) => setPersonOptions((as || []).map((a) => ({ _id: a._id, name: a.name })))).catch(() => setPersonOptions([]));

    if (editing) {
      setTitle(editing.title);
      setDescription(editing.description || '');
      setAmount(String(editing.amount));
      setType(editing.type);
      setPersonSel(editing.person || null);
      setProjectSel(editing.project ? { _id: editing.project._id, name: editing.project.name || '' } : null);
      setCategory(editing.category || '');
      setMethod(editing.method || '');
      setTags(editing.tags || []);
      setDate(toDateInput(editing.date));
      setExistingReceipts(editing.receipts || []);
      setReminderEnabled(!!editing.reminderAt);
      setReminderAt(toLocalDateTimeInput(editing.reminderAt));
    } else {
      setTitle(''); setDescription(''); setAmount(''); setType('expense');
      setPersonSel(null); setProjectSel(null); setCategory(''); setMethod(''); setTags([]);
      setDate(toDateInput()); setExistingReceipts([]); setReminderEnabled(false); setReminderAt('');
    }
    setRemoveReceiptIds([]); setNewReceipts([]); setError(''); setFieldErrors({});
  }, [open, editing]);

  const typeMeta = TYPE_META[type];

  const handleFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const arr = Array.from(files);
    const valid = arr.filter((f) => f.size <= MAX_UPLOAD_BYTES);
    if (valid.length < arr.length) Swal.fire('Invalid files', 'Receipts must be 10MB or smaller per file.', 'error');
    setNewReceipts((prev) => [...prev, ...valid]);
  };

  const personName = (v: OptionItem | string | null) => (typeof v === 'string' ? v : v?.name || '');

  const handleSave = async () => {
    const amountNum = Number(amount);
    const errs: typeof fieldErrors = {};
    if (!title.trim()) errs.title = 'Title is required';
    else if (title.trim().length > 120) errs.title = 'Title must be 120 characters or fewer';
    if (!amount.trim()) errs.amount = 'Enter the amount';
    else if (!Number.isFinite(amountNum) || amountNum <= 0) errs.amount = 'Amount must be greater than 0';
    if (!date) errs.date = 'Choose a date';
    if (description.trim().length > 500) errs.description = 'Note must be 500 characters or fewer';
    if (isEdit && reminderEnabled && !reminderAt) errs.reminderAt = 'Pick a date and time for the reminder';
    setFieldErrors(errs);
    if (Object.keys(errs).length) return;
    setError('');

    const fd = new FormData();
    fd.append('title', title.trim());
    fd.append('description', description.trim());
    fd.append('amount', String(amountNum));
    fd.append('type', type);
    fd.append('person', personName(personSel));
    if (projectSel) fd.append('project', projectSel._id);
    if (category) fd.append('category', category);
    if (method) fd.append('method', method);
    tags.forEach((t) => fd.append('tags[]', t));
    fd.append('date', new Date(date).toISOString());
    if (isEdit) {
      fd.append('reminderEnabled', String(reminderEnabled));
      if (reminderEnabled && reminderAt) fd.append('reminderAt', new Date(reminderAt).toISOString());
      removeReceiptIds.forEach((id) => fd.append('removeReceiptIds[]', id));
    }
    newReceipts.forEach((f) => fd.append('receipts', f));

    setSaving(true);
    try {
      if (editing) await updatePayment(editing._id, fd);
      else await createPayment(fd);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to save payment');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editing) return;
    const result = await Swal.fire({ title: 'Delete payment?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete' });
    if (!result.isConfirmed) return;
    await deletePayment(editing._id);
    onSaved();
    onClose();
  };

  const accent = typeMeta.color;
  const [dragOver, setDragOver] = useState(false);
  const hasReceipts = existingReceipts.length > 0 || newReceipts.length > 0;

  const inputRadius = {
    '& .MuiOutlinedInput-root': { borderRadius: '14px' },
    '& .MuiOutlinedInput-root fieldset': { borderWidth: 2, borderColor: `${accent}33` },
    '& .MuiOutlinedInput-root:hover fieldset': { borderColor: `${accent}77` },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: accent },
  } as const;

  const receiptRow = (key: string, name: React.ReactNode, size: string, onRemove: () => void) => (
    <Box
      key={key}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: '14px',
        bgcolor: 'background.paper', border: '1px solid', borderColor: `${accent}33`,
      }}
    >
      <Box
        sx={{
          width: 38, height: 38, borderRadius: '12px', display: 'grid', placeItems: 'center', flexShrink: 0, color: '#fff',
          background: `linear-gradient(135deg, ${accent}, ${accent}B3)`,
        }}
      >
        <ImageRounded fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {name}
        <Typography variant="caption" color="text.secondary">{size}</Typography>
      </Box>
      <IconButton size="small" onClick={onRemove}><CloseRounded fontSize="small" /></IconButton>
    </Box>
  );

  return (
    <FormDialogShell
      open={open}
      onClose={onClose}
      colors={typeMeta.grad}
      maxWidth="sm"
      icon={isEdit ? <EditRounded /> : <AddRounded />}
      title={isEdit ? 'Edit Payment' : 'Add Payment'}
      subtitle={isEdit ? 'Update the payment details' : 'Add income, expense or transfer in seconds'}
      footer={
        <>
          {isEdit && (
            <Button
              onClick={handleDelete}
              startIcon={<DeleteRounded />}
              sx={{ borderRadius: '999px', px: 2, color: '#DC2626', border: '2px solid #DC262655', '&:hover': { bgcolor: '#DC262614' } }}
            >
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onClose}
            sx={{ borderRadius: '999px', px: 2.5, border: '2px solid', borderColor: 'divider', color: 'text.primary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            startIcon={<SendRounded fontSize="small" />}
            sx={gradientButtonSx(typeMeta.grad)}
          >
            {saving ? 'Saving…' : isEdit ? 'Update Payment' : 'Save Payment'}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Type switch */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
          {(Object.keys(TYPE_META) as Array<keyof typeof TYPE_META>).map((k) => {
            const meta = TYPE_META[k];
            const active = type === k;
            const Icon = meta.icon;
            return (
              <Box
                key={k}
                component="button"
                type="button"
                onClick={() => setType(k)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  py: 1.4, cursor: 'pointer', borderRadius: '18px', fontFamily: 'inherit',
                  fontWeight: 800, fontSize: 14,
                  color: active ? '#fff' : meta.color,
                  border: '2px solid', borderColor: active ? meta.color : `${meta.color}44`,
                  background: active ? `linear-gradient(135deg, ${meta.grad[0]}, ${meta.grad[1]})` : meta.bg,
                  boxShadow: active ? `0 12px 24px ${meta.color}55, inset 0 1px 0 rgba(255,255,255,.4)` : 'none',
                  transform: active ? 'translateY(-2px)' : 'none',
                  transition: 'all .18s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}
              >
                <Icon />
                {meta.label}
              </Box>
            );
          })}
        </Box>

        {/* Amount hero */}
        <Box
          sx={{
            position: 'relative', overflow: 'hidden', borderRadius: '22px', p: 2,
            display: 'flex', alignItems: 'center', gap: 1.5,
            background: `linear-gradient(135deg, ${typeMeta.bg}, #ffffff)`,
            border: '2px solid', borderColor: `${accent}55`,
            boxShadow: `0 12px 26px ${accent}22`,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: accent, letterSpacing: 0.6, textTransform: 'uppercase' }}>
              Amount
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              value={amount}
              onChange={(e) => { if (/^\d*\.?\d*$/.test(e.target.value)) { setAmount(e.target.value); clearField('amount'); } }}
              placeholder="0"
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{
                disableUnderline: true,
                startAdornment: <Typography sx={{ fontWeight: 900, fontSize: 34, mr: 0.75, color: accent }}>₹</Typography>,
                sx: { fontSize: 40, fontWeight: 900, color: accent },
              }}
            />
          </Box>
          <IconButton
            onClick={(e) => setCalcAnchor(e.currentTarget)}
            aria-label="Open calculator"
            sx={{
              color: '#fff', width: 58, height: 58, borderRadius: '18px',
              background: `linear-gradient(135deg, ${typeMeta.grad[0]}, ${typeMeta.grad[1]})`,
              boxShadow: `0 10px 20px ${accent}66, inset 0 1px 0 rgba(255,255,255,.4)`,
              '&:hover': { opacity: 0.92, transform: 'translateY(-1px)' },
            }}
          >
            <CalculateRounded />
          </IconButton>
          <CalculatorPopover anchorEl={calcAnchor} onClose={() => setCalcAnchor(null)} onApply={(v) => setAmount(String(v))} />
        </Box>
        {fieldErrors.amount && (
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mt: -1.25 }}>{fieldErrors.amount}</Typography>
        )}

        {/* Title + note */}
        <FieldCard icon={DescriptionRounded} label="What is it for?" color={accent}>
          <TextField
            fullWidth
            value={title}
            onChange={(e) => { setTitle(e.target.value); clearField('title'); }}
            placeholder="Title (e.g. Electricity bill)"
            error={!!fieldErrors.title}
            helperText={fieldErrors.title}
            sx={inputRadius}
            InputProps={{
              sx: { fontSize: 18, fontWeight: 700 },
              endAdornment: title ? (
                <IconButton size="small" onClick={() => setTitle('')}><CloseRounded fontSize="small" /></IconButton>
              ) : undefined,
            }}
          />
          <TextField
            fullWidth
            multiline
            minRows={1}
            value={description}
            onChange={(e) => { setDescription(e.target.value); clearField('description'); }}
            placeholder="Add a note (optional)..."
            error={!!fieldErrors.description}
            helperText={fieldErrors.description}
            sx={{ mt: 1.25, ...inputRadius }}
            InputProps={{
              startAdornment: <ChatBubbleOutlineRounded fontSize="small" sx={{ color: 'text.secondary', mr: 1, alignSelf: 'flex-start', mt: 0.5 }} />,
              endAdornment: description ? (
                <IconButton size="small" onClick={() => setDescription('')}><CloseRounded fontSize="small" /></IconButton>
              ) : undefined,
            }}
          />
        </FieldCard>

        {/* Categories — all visible as colourful pills */}
        <FieldCard icon={CategoryRounded} label="Category" color="#6226A7">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.9 }}>
            {CATEGORIES.map((c) => {
              const active = category === c.name;
              const Icon = c.icon;
              return (
                <Box
                  key={c.name}
                  component="button"
                  type="button"
                  onClick={() => setCategory(active ? '' : c.name)}
                  sx={{
                    cursor: 'pointer', fontFamily: 'inherit',
                    display: 'inline-flex', alignItems: 'center', gap: 0.6,
                    px: 1.4, py: 0.7, borderRadius: '999px', fontWeight: 800, fontSize: 13,
                    border: '2px solid', borderColor: active ? c.color : `${c.color}44`,
                    color: active ? '#fff' : c.color,
                    background: active ? `linear-gradient(135deg, ${c.color}, ${c.color}B3)` : `${c.color}12`,
                    boxShadow: active ? `0 8px 16px ${c.color}55, inset 0 1px 0 rgba(255,255,255,.4)` : 'none',
                    transition: 'all .18s ease',
                    '&:hover': { transform: 'translateY(-1px)' },
                  }}
                >
                  <Icon sx={{ fontSize: 17 }} />
                  {c.name}
                </Box>
              );
            })}
          </Box>
        </FieldCard>

        {/* Details */}
        <FieldCard icon={EventRounded} label="Details" color="#0EA5E9">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
            <TextField
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); clearField('date'); }}
              error={!!fieldErrors.date}
              helperText={fieldErrors.date}
              sx={inputRadius}
              InputProps={{ startAdornment: <CalendarTodayRounded fontSize="small" sx={{ color: accent, mr: 1 }} /> }}
            />
            <Autocomplete
              freeSolo
              options={personOptions}
              value={personSel}
              onChange={(_, v) => setPersonSel(v)}
              onInputChange={(_, v, reason) => { if (reason === 'input') setPersonSel(v); }}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Person"
                  sx={inputRadius}
                  InputProps={{ ...params.InputProps, startAdornment: <PersonRounded fontSize="small" sx={{ color: accent, ml: 0.5, mr: 0.5 }} /> }}
                />
              )}
            />
            <Autocomplete
              options={projectOptions}
              value={projectSel}
              onChange={(_, v) => setProjectSel(v)}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o._id === v._id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Project (optional)"
                  sx={inputRadius}
                  InputProps={{ ...params.InputProps, startAdornment: <FolderRounded fontSize="small" sx={{ color: '#2563EB', ml: 0.5, mr: 0.5 }} /> }}
                />
              )}
            />
            <FormControl fullWidth sx={inputRadius}>
              <Select
                displayEmpty
                value={method}
                onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                renderValue={(v) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CreditCardRounded fontSize="small" sx={{ color: accent }} />
                    {v || <Box component="span" sx={{ color: 'text.disabled' }}>Payment method</Box>}
                  </Box>
                )}
              >
                {PAYMENT_METHODS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </FieldCard>

        {/* Tags */}
        <FieldCard icon={LocalOfferRounded} label="Tags" color="#EC4899">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
            {tags.map((t) => (
              <Chip
                key={t}
                label={`#${t}`}
                onDelete={() => setTags((prev) => prev.filter((x) => x !== t))}
                sx={{ bgcolor: '#EC489914', color: '#BE185D', fontWeight: 700 }}
              />
            ))}
            <TagAdder onAdd={(t) => setTags((prev) => (prev.includes(t) ? prev : [...prev, t]))} />
          </Box>
        </FieldCard>

        {/* Receipts drop zone */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
          sx={{
            p: 1.75, borderRadius: '18px', border: '2px dashed',
            borderColor: dragOver ? accent : `${accent}66`,
            bgcolor: dragOver ? `${accent}14` : 'transparent',
            transition: 'all .2s ease',
          }}
        >
          {hasReceipts && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              {existingReceipts.map((r) => receiptRow(
                r._id,
                <MLink href={r.url} target="_blank" rel="noopener" underline="hover" sx={{ fontWeight: 700, display: 'block' }} noWrap>
                  {r.filename || 'receipt'}
                </MLink>,
                fmtSize(r.size),
                () => { setExistingReceipts((prev) => prev.filter((x) => x._id !== r._id)); setRemoveReceiptIds((prev) => [...prev, r._id]); },
              ))}
              {newReceipts.map((f, i) => receiptRow(
                `${f.name}-${i}`,
                <Typography sx={{ fontWeight: 700 }} noWrap>{f.name}</Typography>,
                fmtSize(f.size),
                () => setNewReceipts((prev) => prev.filter((_, idx) => idx !== i)),
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
            <Button
              onClick={() => fileInputRef.current?.click()}
              startIcon={hasReceipts ? <AttachFileRounded /> : <ImageRounded />}
              sx={{
                color: '#fff', px: 2, borderRadius: '999px',
                background: `linear-gradient(135deg, ${typeMeta.grad[0]}, ${typeMeta.grad[1]})`,
                boxShadow: `0 8px 18px ${accent}55, inset 0 1px 0 rgba(255,255,255,.4)`,
              }}
            >
              {hasReceipts ? 'Add another file' : 'Add receipt'}
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 140 }}>
              images or PDF · drag &amp; drop · up to 10&nbsp;MB
            </Typography>
          </Box>
          <input ref={fileInputRef} type="file" hidden multiple accept="image/*,application/pdf" onChange={(e) => { handleFiles(e.target.files); e.target.value = ''; }} />
        </Box>

        {isEdit && (
          <FieldCard icon={NotificationsActiveRounded} label="Reminder" color="#F59E0B">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Switch checked={reminderEnabled} onChange={(e) => setReminderEnabled(e.target.checked)} color="warning" />
              {reminderEnabled ? (
                <TextField
                  size="small"
                  type="datetime-local"
                  value={reminderAt}
                  onChange={(e) => { setReminderAt(e.target.value); clearField('reminderAt'); }}
                  error={!!fieldErrors.reminderAt}
                  helperText={fieldErrors.reminderAt}
                  sx={{ flex: 1, minWidth: 220, ...inputRadius }}
                  InputProps={{
                    endAdornment: reminderAt ? (
                      <IconButton size="small" onClick={() => setReminderAt('')}><CloseRounded fontSize="small" /></IconButton>
                    ) : undefined,
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">Turn on to get reminded about this payment</Typography>
              )}
            </Box>
          </FieldCard>
        )}

        {error && (
          <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700 }}>{error}</Typography>
        )}
      </Box>
    </FormDialogShell>
  );
};

const TagAdder = ({ onAdd }: { onAdd: (tag: string) => void }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (!editing) {
    return (
      <Chip
        icon={<LocalOfferRounded sx={{ fontSize: 16 }} />}
        label="Add tag"
        onClick={() => setEditing(true)}
        variant="outlined"
        sx={{ borderColor: '#EC489966', color: '#BE185D', fontWeight: 700 }}
      />
    );
  }

  const commit = () => {
    const v = value.trim();
    if (v) onAdd(v);
    setValue('');
    setEditing(false);
  };

  return (
    <TextField
      autoFocus
      size="small"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      placeholder="Tag name"
      sx={{ width: 140 }}
    />
  );
};

export default PaymentFormDialog;
